import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  BRAND_COLUMN_MAX_WIDTH,
  BRAND_COLUMN_MIN_WIDTH,
  DESCRIPTION_COLUMN_MAX_WIDTH,
  FULFILLED_COLUMN_MAX_WIDTH,
  FULFILLED_COLUMN_MIN_WIDTH,
  GP_DOLLAR_COLUMN_MAX_WIDTH,
  GP_DOLLAR_COLUMN_MIN_WIDTH,
  GP_PERCENTAGE_COLUMN_MAX_WIDTH,
  GP_PERCENTAGE_MIN_COLUMN_SIZE,
  ITEM_CODE_COLUMN_MAX_WIDTH,
  ITEM_CODE_COLUMN_MIN_WIDTH,
  MIN_COLUMN_SIZE,
  MIN_DESCRIPTION_COLUMN_SIZE,
  MIN_NAME_COLUMN_SIZE,
  NAME_COLUMN_PERCENTAGE,
  NOTES_COLUMN_MAX_WIDTH,
  NOTES_COLUMN_MIN_WIDTH,
  PACK_SIZE_COLUMN_MAX_WIDTH,
  PACK_SIZE_COLUMN_MIN_WIDTH,
  PRICE_COLUMN_MAX_WIDTH,
  PRICE_COLUMN_MAX_WIDTH_WITH_UNIT,
  PRICE_COLUMN_MIN_WIDTH,
  PRICE_EDIT_DISPLAY_COLUMN_MAX_WIDTH,
  PRICE_EDIT_DISPLAY_COLUMN_MIN_WIDTH,
  QUANTITY_COLUMN_WIDTH,
  UNIT_COLUMN_MAX_WIDTH,
  UNIT_COLUMN_MIN_WIDTH,
} from './order-entry-table-constants';
import {
  ColumnDef,
  createColumnHelper,
  VisibilityState,
} from '@tanstack/react-table';
import {
  ColumnId,
  ON_HAND_COLUMN_KEYS,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { compareFulfillmentDates } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { CustomColumnMeta } from '@experiences/components/table/types';
import { getOrderEntryConfigurableColumns } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/utils/configurable-columns-utils';
import { getRenderedRowIndex } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-utils';
import { getTranslationValue } from '@/utils/translations';
import { OrderEntryInputCellShortcutCallbacks } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-order-entry-cell-shortcuts';
import { OrderEntryOnHandCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-on-hand-cell';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import {
  OrderEntryRequestNote,
  OrderEntryTableRowData,
  RequestNoteSavePayload,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { OrderEntryTableGpDollarCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-gp-dollar-cell';
import { OrderEntryTableGrossProfitCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-gross-profit-cell';
import { OrderEntryTableItemNameCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-item-name-cell';
import { OrderEntryTableNotesCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-notes-cell';
import { OrderEntryTablePriceCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-price-cell';
import { OrderEntryTableQuantityCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-quantity-cell';
import { OrderEntryTableReadOnlyGrossProfitCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-read-only-gross-profit-cell';
import { OrderEntryTableReadOnlyPriceCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-read-only-price-cell';
import { OrderEntryTableTooltipTextCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-tooltip-text-cell';
import { OrderEntryTableTrendCell } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-trend-cell';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useConfidentialVisibilityStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/order-entry-confidential-visibility-store';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useTheme } from '@/contexts/theme-context';
import type { LastPurchaseInfoMap } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';

const columnHelper = createColumnHelper<OrderEntryTableRowData>();

interface UseOrderEntryColumnsParams {
  areSubstitutionsEnabled?: boolean;
  canSetManualOverrideExpiration?: boolean;
  canWriteRequestNote?: boolean;
  data: OrderEntryTableRowData[];
  getOrderGuideLabels?: (
    row: OrderEntryTableRowData,
  ) => { level: string; orderGuideName: string }[];
  isGrossProfitHidden?: boolean;
  isPriceEditingEnabled?: boolean;
  lastPurchaseInfoMap?: LastPurchaseInfoMap;
  minMaxQuantityEnabled?: boolean;
  onOpenRestaurantSelector?: () => void;
  onPriceValidationError: (message: string) => void;
  onSaveCart: () => void;
  onSavePriceEdit: (editor: PriceEditor, variantPackUUID: string) => void;
  onSaveRequestNote?: (payload: RequestNoteSavePayload) => Promise<void>;
  onSwitchBuildReviewMode?: () => void;
  onToggleCheckout?: () => void;
  onTriggerSearch?: () => void;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  quantityInputDisabled?: boolean;
  selectedCollectionId?: string;
  showPriceOverrideInfo?: boolean;
}

interface UseOrderEntryColumnsResult {
  columnOrder: string[];
  columns: ColumnDef<
    OrderEntryTableRowData,
    OrderEntryTableRowData[keyof OrderEntryTableRowData]
  >[];
  columnVisibility: VisibilityState;
  pinnedColumns: { left: ColumnId[] };
}

/**
 * Column definitions and pinning configuration for order entry tables.
 *
 * Used by both OrderEntryTable and CatalogSearchTable.
 */
export const useOrderEntryTableConfig = ({
  areSubstitutionsEnabled,
  canSetManualOverrideExpiration,
  data,
  getOrderGuideLabels,
  isGrossProfitHidden,
  canWriteRequestNote,
  isPriceEditingEnabled,
  lastPurchaseInfoMap,
  minMaxQuantityEnabled = true,
  onOpenRestaurantSelector,
  onPriceValidationError,
  onSaveCart,
  onSaveRequestNote,
  onSwitchBuildReviewMode,
  onToggleCheckout,
  onTriggerSearch,
  priceEditDisplayHeaders,
  onSavePriceEdit,
  quantityInputDisabled,
  selectedCollectionId,
  showPriceOverrideInfo,
}: UseOrderEntryColumnsParams): UseOrderEntryColumnsResult => {
  const theme = useTheme();

  const showConfidentialFields = useConfidentialVisibilityStore(
    (state) => state.showConfidentialFields,
  );

  const itemsHaveUnitPrices = useMemo(() => {
    return data.some((row) => row.unitPriceInfo);
  }, [data]);

  // Price column max width depends on whether unit prices are displayed
  const priceColumnMaxWidth = itemsHaveUnitPrices
    ? PRICE_COLUMN_MAX_WIDTH_WITH_UNIT
    : PRICE_COLUMN_MAX_WIDTH;

  // Price column min width: editable cells need more room for the input field
  const priceColumnMinWidth =
    isPriceEditingEnabled && itemsHaveUnitPrices
      ? PRICE_COLUMN_MIN_WIDTH
      : MIN_COLUMN_SIZE;

  // Calculate column percentages based on number of price edit columns
  // Regular columns: Brand, Unit, Item Code, Pack Size, Price, GP%, GP$, Fulfilled, + dynamic price edit columns
  const fulfilledColumnCount = lastPurchaseInfoMap ? 1 : 0;
  const numberOfRegularColumns =
    7 + fulfilledColumnCount + priceEditDisplayHeaders.length;
  const totalShares = numberOfRegularColumns + 2; // +2 for Description's double share
  const remainingPercentage = 100 - NAME_COLUMN_PERCENTAGE;
  const REGULAR_COLUMN_PERCENTAGE = remainingPercentage / totalShares;
  const DESCRIPTION_COLUMN_PERCENTAGE = REGULAR_COLUMN_PERCENTAGE * 2;

  // Dynamic price-edit columns (fixed width for density)
  const priceEditDisplayColumns = useMemo<
    ColumnDef<OrderEntryTableRowData, string | null>[]
  >(() => {
    return priceEditDisplayHeaders.map((header) => {
      const isOnHandColumn = ON_HAND_COLUMN_KEYS.has(header.key);
      return columnHelper.accessor(
        (row) => row.priceEditDisplays[header.key]?.display ?? null,
        {
          cell: (info) =>
            isOnHandColumn && areSubstitutionsEnabled ? (
              <OrderEntryOnHandCell
                displayValue={info.getValue()}
                onHandQuantity={info.row.original.onHandQuantity}
                variantPackUUID={info.row.original.variantPackUUID}
              />
            ) : (
              <OrderEntryTableTrendCell
                displayValue={info.getValue()}
                trend={info.row.original.priceEditDisplays[header.key]?.trend}
              />
            ),
          enableGlobalFilter: false,
          header: () => getTranslationValue(header.translation),
          id: header.key,
          maxSize: PRICE_EDIT_DISPLAY_COLUMN_MAX_WIDTH,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            isConfidential: header.is_confidential,
          },
          minSize: PRICE_EDIT_DISPLAY_COLUMN_MIN_WIDTH,
          sortingFn: (rowA, rowB) => {
            const a = rowA.original.priceEditDisplays[header.key]?.raw;
            const b = rowB.original.priceEditDisplays[header.key]?.raw;

            if (a === null || a === undefined) {
              if (b === null || b === undefined) {
                return 0;
              }
              return -1;
            }
            if (b === null || b === undefined) {
              return 1;
            }

            if (typeof a === 'number' && typeof b === 'number') {
              return a - b;
            }

            return String(a).localeCompare(String(b));
          },
        },
      );
    });
  }, [
    areSubstitutionsEnabled,
    REGULAR_COLUMN_PERCENTAGE,
    priceEditDisplayHeaders,
  ]);

  const shortcutCallbacks = useMemo<OrderEntryInputCellShortcutCallbacks>(
    () => ({
      onOpenRestaurantSelector,
      onSwitchBuildReviewMode,
      onToggleCheckout,
      onTriggerSearch,
    }),
    [
      onOpenRestaurantSelector,
      onSwitchBuildReviewMode,
      onToggleCheckout,
      onTriggerSearch,
    ],
  );

  // All columns including static and dynamic
  const columns = useMemo<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ColumnDef<OrderEntryTableRowData, any>[]
  >(
    () => [
      // Quantity input column
      columnHelper.accessor((row) => row.variantPackUUID, {
        cell: ({ getValue, row, ...context }) => (
          <OrderEntryTableQuantityCell
            disabled={quantityInputDisabled}
            maxQuantity={minMaxQuantityEnabled ? row.original.maxQuantity : 0}
            minQuantity={minMaxQuantityEnabled ? row.original.minQuantity : 0}
            onSaveCart={onSaveCart}
            rowIndex={getRenderedRowIndex(context, row.index)}
            shortcutCallbacks={shortcutCallbacks}
            variantPackUUID={getValue()}
          />
        ),
        enableGlobalFilter: false,
        enableSorting: false,
        header: () => '',
        id: ColumnId.Quantity,
        maxSize: QUANTITY_COLUMN_WIDTH,
        minSize: QUANTITY_COLUMN_WIDTH,
        size: QUANTITY_COLUMN_WIDTH,
      }),
      // Name (special wide column)
      columnHelper.accessor((row) => row.name, {
        cell: (info) => (
          <OrderEntryTableItemNameCell
            imageUrl={info.row.original.imageUrl}
            labels={info.row.original.labels}
            name={info.getValue()}
            orderGuideLabels={getOrderGuideLabels?.(info.row.original) ?? []}
          />
        ),
        header: () => i18n.t('sales_portal.order_entry.table.name'),
        id: ColumnId.Name,
        meta: {
          initialPercentWidth: NAME_COLUMN_PERCENTAGE,
        },
        minSize: MIN_NAME_COLUMN_SIZE,
        sortingFn: 'alphanumeric',
      }),
      // Unit (fixed width for density)
      columnHelper.accessor((row) => row.packDisplayString, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        enableGlobalFilter: false,
        header: () => i18n.t('sales_portal.order_entry.table.unit'),
        id: ColumnId.Unit,
        maxSize: UNIT_COLUMN_MAX_WIDTH,
        meta: { initialPercentWidth: REGULAR_COLUMN_PERCENTAGE },
        minSize: UNIT_COLUMN_MIN_WIDTH,
      }),
      // Item code (fixed width for density)
      columnHelper.accessor((row) => row.externalItemId, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            textTransform="uppercase"
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => i18n.t('sales_portal.order_entry.table.item_code'),
        id: ColumnId.ItemCode,
        maxSize: ITEM_CODE_COLUMN_MAX_WIDTH,
        meta: { initialPercentWidth: REGULAR_COLUMN_PERCENTAGE },
        minSize: ITEM_CODE_COLUMN_MIN_WIDTH,
      }),
      // Price
      columnHelper.accessor((row) => row.priceDisplayString, {
        cell: ({ row, getValue, ...context }) => {
          const { priceOverrideInfo } = row.original;
          const rowIndex = getRenderedRowIndex(context, row.index);

          if (!isPriceEditingEnabled) {
            return (
              <OrderEntryTableReadOnlyPriceCell
                canSetManualOverrideExpiration={canSetManualOverrideExpiration}
                priceDisplayString={getValue()}
                priceOverrideInfo={priceOverrideInfo}
                showPriceOverrideInfo={showPriceOverrideInfo}
                unitCount={row.original.unitCount}
                unitPriceInfo={row.original.unitPriceInfo}
              />
            );
          }

          const unitPriceInfoStr = row.original.unitPriceInfo;
          const unitSuffix = row.original.unitSuffix;

          return (
            <OrderEntryTablePriceCell
              canSetManualOverrideExpiration={canSetManualOverrideExpiration}
              onSave={onSavePriceEdit}
              onValidationError={onPriceValidationError}
              priceDisplayString={row.original.priceDisplayString}
              priceOverrideInfo={priceOverrideInfo}
              rowIndex={rowIndex}
              shortcutCallbacks={shortcutCallbacks}
              unitCount={row.original.unitCount}
              unitPriceInfo={unitPriceInfoStr}
              unitSuffix={unitSuffix}
              variantPackUUID={row.original.variantPackUUID}
            />
          );
        },
        enableGlobalFilter: false,
        header: () => i18n.t('sales_portal.order_entry.table.price'),
        id: ColumnId.Price,
        maxSize: priceColumnMaxWidth,
        meta: { initialPercentWidth: REGULAR_COLUMN_PERCENTAGE },
        minSize: priceColumnMinWidth,
        sortingFn: (rowA, rowB) => {
          if (rowA.original.priceMicros === null) {
            return -1;
          }
          if (rowB.original.priceMicros === null) {
            return 1;
          }
          return rowA.original.priceMicros - rowB.original.priceMicros;
        },
      }),
      // GP %
      // Accessor returns the raw number (or undefined for null/0) so TanStack's
      // `sortUndefined: 'last'` keeps empty values at the bottom in both
      // sort directions. 0 is treated as missing data — it usually means the
      // backend hasn't computed cost. The display string is read from
      // row.original below.
      columnHelper.accessor((row) => row.grossProfitPercentage || undefined, {
        cell: ({ row, ...context }) => {
          const { priceOverrideInfo } = row.original;
          const rowIndex = getRenderedRowIndex(context, row.index);

          if (!isPriceEditingEnabled) {
            return (
              <OrderEntryTableReadOnlyGrossProfitCell
                canSetManualOverrideExpiration={canSetManualOverrideExpiration}
                grossProfitDisplayString={
                  row.original.grossProfitPercentageDisplayString
                }
                priceOverrideInfo={priceOverrideInfo}
                showPriceOverrideInfo={showPriceOverrideInfo}
              />
            );
          }

          return (
            <OrderEntryTableGrossProfitCell
              canSetManualOverrideExpiration={canSetManualOverrideExpiration}
              grossProfitDisplayString={
                row.original.grossProfitPercentageDisplayString
              }
              onSave={onSavePriceEdit}
              onValidationError={onPriceValidationError}
              priceOverrideInfo={priceOverrideInfo}
              rowIndex={rowIndex}
              shortcutCallbacks={shortcutCallbacks}
              variantPackUUID={row.original.variantPackUUID}
            />
          );
        },
        enableGlobalFilter: false,
        header: () => i18n.t('sales_portal.order_entry.table.gp_percentage'),
        id: ColumnId.GpPercentage,
        maxSize: GP_PERCENTAGE_COLUMN_MAX_WIDTH,
        meta: {
          initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
          isConfidential: true,
        },
        minSize: GP_PERCENTAGE_MIN_COLUMN_SIZE,
        sortDescFirst: false,
        sortingFn: 'basic',
        sortUndefined: 'last',
      }),
      // GP $
      // 0 is treated as missing data, same as GP% above.
      columnHelper.accessor(
        (row) => row.grossProfitAmountInMicros || undefined,
        {
          cell: ({ row }) => {
            if (!isPriceEditingEnabled) {
              return (
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-md"
                >
                  {row.original.grossProfitDollarDisplayString}
                </Typography>
              );
            }

            return (
              <OrderEntryTableGpDollarCell
                grossProfitDollarDisplayString={
                  row.original.grossProfitDollarDisplayString
                }
                variantPackUUID={row.original.variantPackUUID}
              />
            );
          },
          enableGlobalFilter: false,
          header: () => i18n.t('sales_portal.order_entry.table.gp_dollar'),
          id: ColumnId.GpDollar,
          maxSize: GP_DOLLAR_COLUMN_MAX_WIDTH,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            isConfidential: true,
          },
          minSize: GP_DOLLAR_COLUMN_MIN_WIDTH,
          sortDescFirst: false,
          sortingFn: 'basic',
          sortUndefined: 'last',
        },
      ),
      // Fulfilled (last purchase info — only shown when data is available)
      ...(lastPurchaseInfoMap
        ? [
            columnHelper.accessor(
              (row): string | undefined => {
                const info = lastPurchaseInfoMap[row.variantPackUUID];
                return info?.fulfillmentDate ?? info?.orderDate ?? undefined;
              },
              {
                cell: ({ row }) => (
                  <Typography
                    color={theme.sk.color.text.primary}
                    variant="paragraph-md"
                  >
                    {lastPurchaseInfoMap[row.original.variantPackUUID]
                      ?.display ?? ''}
                  </Typography>
                ),
                enableGlobalFilter: false,
                header: () =>
                  i18n.t(
                    'sales_portal.order_entry.table.fulfilled_column_header',
                  ),
                id: ColumnId.Fulfilled,
                maxSize: FULFILLED_COLUMN_MAX_WIDTH,
                meta: {
                  initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
                },
                minSize: FULFILLED_COLUMN_MIN_WIDTH,
                sortingFn: (rowA, rowB) =>
                  compareFulfillmentDates(
                    lastPurchaseInfoMap[rowA.original.variantPackUUID],
                    lastPurchaseInfoMap[rowB.original.variantPackUUID],
                  ),
                // Rows without a purchase date sort to the bottom regardless
                // of sort direction — tanstack short-circuits these before
                // the custom sortingFn runs.
                sortUndefined: 'last',
              },
            ),
          ]
        : []),
      // Description (double width)
      columnHelper.accessor((row) => row.description, {
        cell: (info) => (
          <OrderEntryTableTooltipTextCell text={info.getValue()} />
        ),
        enableGlobalFilter: false,
        header: () => i18n.t('sales_portal.order_entry.table.description'),
        id: ColumnId.Description,
        maxSize: DESCRIPTION_COLUMN_MAX_WIDTH,
        meta: {
          initialPercentWidth: DESCRIPTION_COLUMN_PERCENTAGE,
        },
        minSize: MIN_DESCRIPTION_COLUMN_SIZE,
      }),
      // Notes column — only rendered when notes are enabled for this supplier.
      // Each row carries a `requestNotes` array which has one entry per order guide
      // collection that the vp is part of. The accessor finds the note matching the currently
      // selected collection so the cell can display the relevant note.
      ...(canWriteRequestNote
        ? [
            columnHelper.accessor(
              // Resolve the matching note for the selected order guide.
              // Returns the full note object so the cell can read both
              // `request` (text) and `persistRequest` (toggle) via getValue().
              (row): OrderEntryRequestNote | null => {
                if (!selectedCollectionId) {
                  return null;
                }
                return (
                  row.requestNotes.find(
                    (n) => n.collectionId === selectedCollectionId,
                  ) ?? null
                );
              },
              {
                cell: ({ getValue, row }) => {
                  const note = getValue();
                  return (
                    <OrderEntryTableNotesCell
                      collectionId={selectedCollectionId ?? ''}
                      onSave={onSaveRequestNote}
                      persistRequest={note?.persistRequest ?? false}
                      request={note?.request ?? null}
                      variantPackUUID={row.original.variantPackUUID}
                    />
                  );
                },
                enableGlobalFilter: false,
                header: () => i18n.t('sales_portal.order_entry.table.notes'),
                id: ColumnId.Notes,
                maxSize: NOTES_COLUMN_MAX_WIDTH,
                meta: {
                  initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
                },
                minSize: NOTES_COLUMN_MIN_WIDTH,
              },
            ),
          ]
        : []),
      // Brand
      columnHelper.accessor((row) => row.brand, {
        cell: (info) => (
          <OrderEntryTableTooltipTextCell text={info.getValue()} />
        ),
        enableGlobalFilter: false,
        header: () => i18n.t('sales_portal.order_entry.table.brand'),
        id: ColumnId.Brand,
        maxSize: BRAND_COLUMN_MAX_WIDTH,
        meta: { initialPercentWidth: REGULAR_COLUMN_PERCENTAGE },
        minSize: BRAND_COLUMN_MIN_WIDTH,
      }),
      // Pack Size
      columnHelper.accessor((row) => row.packSize, {
        cell: (info) => (
          <OrderEntryTableTooltipTextCell text={info.getValue()} />
        ),
        enableGlobalFilter: false,
        header: () => i18n.t('sales_portal.order_entry.table.pack_size'),
        id: ColumnId.PackSize,
        maxSize: PACK_SIZE_COLUMN_MAX_WIDTH,
        meta: { initialPercentWidth: REGULAR_COLUMN_PERCENTAGE },
        minSize: PACK_SIZE_COLUMN_MIN_WIDTH,
      }),
      // Dynamic price-edit columns
      ...priceEditDisplayColumns,
      // Hidden column for sorting review tab items by when they were added to cart
      columnHelper.accessor((row) => row.addedAt ?? '', {
        enableGlobalFilter: false,
        header: () => '',
        id: ColumnId.AddedAt,
      }),
    ],
    [
      DESCRIPTION_COLUMN_PERCENTAGE,
      REGULAR_COLUMN_PERCENTAGE,
      getOrderGuideLabels,
      canSetManualOverrideExpiration,
      canWriteRequestNote,
      isPriceEditingEnabled,
      lastPurchaseInfoMap,
      minMaxQuantityEnabled,
      priceColumnMinWidth,
      onPriceValidationError,
      onSaveCart,
      onSavePriceEdit,
      onSaveRequestNote,
      priceColumnMaxWidth,
      priceEditDisplayColumns,
      quantityInputDisabled,
      selectedCollectionId,
      shortcutCallbacks,
      showPriceOverrideInfo,
      theme,
    ],
  );

  // Thin meta-only column-def list driving the configure-columns popover.
  // Order-entry calls this here (and the screen calls it again for the
  // popover button); both calls use the same params, so the persisted
  // preferences apply consistently.
  const configurableColumnsMeta = useMemo(
    () =>
      getOrderEntryConfigurableColumns({
        canWriteRequestNote,
        isFulfilledHidden: !lastPurchaseInfoMap,
        isGrossProfitHidden: Boolean(isGrossProfitHidden),
        priceEditDisplayHeaders,
      }),
    [
      isGrossProfitHidden,
      canWriteRequestNote,
      priceEditDisplayHeaders,
      lastPurchaseInfoMap,
    ],
  );

  const {
    columnOrder: userColumnOrder,
    columnVisibility: userColumnVisibility,
  } = useConfigureColumns(
    ConfigurationDomain.Ordering,
    TableName.OrderEntry,
    configurableColumnsMeta,
  );

  // Merge system-level visibility rules on top of the user's preferences.
  // System rules: AddedAt is always hidden (used for sort only); confidential
  // columns are hidden when the toggle is off; GP columns are hidden by
  // dynamic config (already excluded from the configurable list, but defend).
  const columnVisibility = useMemo<VisibilityState>(() => {
    const visibility: VisibilityState = {
      ...userColumnVisibility,
      [ColumnId.AddedAt]: false,
    };
    for (const col of columns) {
      const meta = col.meta as CustomColumnMeta | undefined;
      const isGpColumn =
        col.id === ColumnId.GpPercentage || col.id === ColumnId.GpDollar;
      const isSystemHidden =
        (isGpColumn && isGrossProfitHidden) ||
        (meta?.isConfidential && !showConfidentialFields);
      if (isSystemHidden) {
        visibility[col.id!] = false;
      }
    }
    return visibility;
  }, [
    userColumnVisibility,
    columns,
    isGrossProfitHidden,
    showConfidentialFields,
  ]);

  // Append column ids that exist on the rendered table but are absent from
  // the configurable list (Fulfilled when present, AddedAt). TanStack
  // requires `state.columnOrder` to be a total ordering; missing ids would
  // get appended at the end, which is what we want for these system columns.
  const columnOrder = useMemo(() => {
    const knownIds = new Set(userColumnOrder);
    const trailing: string[] = [];
    for (const col of columns) {
      if (col.id && !knownIds.has(col.id)) {
        trailing.push(col.id);
      }
    }
    return [...userColumnOrder, ...trailing];
  }, [userColumnOrder, columns]);

  return {
    columnOrder,
    columns,
    columnVisibility,
    pinnedColumns: { left: [ColumnId.Quantity, ColumnId.Name] },
  };
};
